import { camelCase, isNil, noop, snakeCase } from 'lodash-es';
import { call, put } from 'redux-saga/effects';
import { APICallExtraOptions, APICallInterface } from './types';
import getKeysNormalizer from '../../utils/keys-normalizer';
import request from '@host-panel/request';

const defaultOptions = {
  ignoreErrors: false,
  shouldDispatchActions: true
};

// TBD
export function* handleApiError() {
  yield call(noop);
}

export function* handleApiCall(
  {
    types,
    params: { url, method, payload, queryParams, config, useUnAuthorized, customRequestHandler },
    responseMapper = getKeysNormalizer(camelCase),
    queryParamsMapper = getKeysNormalizer(snakeCase),
    meta,
    entireResponseMapper
  }: APICallInterface,
  options: APICallExtraOptions = defaultOptions
) {
  const ignoreErrors = options?.ignoreErrors ?? defaultOptions.ignoreErrors;
  const shouldDispatchActions =
    options?.shouldDispatchActions ?? defaultOptions.shouldDispatchActions;

  if (types?.length !== 3 && shouldDispatchActions) {
    throw new Error('Expected an array of three action types.');
  }

  const [REQUEST_TYPE, SUCCESS_TYPE, FAILURE_TYPE] = types.map(String);

  if (shouldDispatchActions) {
    yield put({ type: REQUEST_TYPE, meta: { ...meta, payload, queryParams } });
  }

  const mappedQueryParams = queryParamsMapper(queryParams);
  let originalResponse = null;

  try {
    originalResponse = yield call(
      // @ts-ignore
      !isNil(customRequestHandler) ? customRequestHandler : request,
      url,
      {
        method,
        url,
        data: payload ?? mappedQueryParams, // FYI: @host-panel/request is getting queryParams from data param @kyivcoder
        params: mappedQueryParams,
        ...config
      },
      useUnAuthorized
    );

    const code = originalResponse?.status;
    const responseData = originalResponse?.data ?? originalResponse; // FYI: @host-panel/request not returning data key @kyivcoder
    const mappedData = responseMapper(responseData);

    if (shouldDispatchActions) {
      yield put({
        type: SUCCESS_TYPE,
        payload: {
          code,
          response: entireResponseMapper ? entireResponseMapper(originalResponse) : mappedData
        },
        meta
      });
    }

    return { response: originalResponse, data: mappedData };
  } catch (rejection) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Api call rejection', rejection);
    }

    const code = rejection?.response?.status;
    const response = rejection?.response?.data;

    if (!ignoreErrors) {
      yield call(handleApiError, {
        action: REQUEST_TYPE,
        code,
        response,
        originalResponse,
        isUnauthorizedRequest: useUnAuthorized,
        params: {
          payload,
          queryParams,
          meta
        },
        rejection
      });
    }

    if (shouldDispatchActions) {
      yield put({
        type: FAILURE_TYPE,
        payload: {
          code,
          response
        },
        meta
      });
    }

    return { error: rejection, response: rejection?.response };
  }
}
